import { OpeningOrderFeeListResponseType } from "@sellar-io/api-v2/types";
import { PaginationState } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { Icon, createSellarColumnHelper } from "@sellar-io/components";
import dayjs from "dayjs";
import { formatCurrency } from "@sellar-io/common";
import { useOpeningOrderFeePaginatedList } from "../../../../hooks/api/v2/openingOrderFee/useOpeningOrderFeeList";

import { SpinnerLegacy } from "../../../../components";
import { TablePaginated } from "@sellar-io/components";

type ColumnDataType = OpeningOrderFeeListResponseType[number];

export const OOFTable = ({
  setSelectedOof,
  setActive,
  search,
}: {
  setSelectedOof: (oof: OpeningOrderFeeListResponseType[number]) => void;
  setActive: (value: boolean) => void;
  search: string;
}) => {
  const [sorting, setSorting] = useState<{ id: string; desc: boolean }[]>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });

  const oofQuery = useOpeningOrderFeePaginatedList({
    limit: pagination.pageSize,
    offset: pagination.pageIndex * pagination.pageSize,
    search,
    sortBy: (sorting[0]?.id as "createdAt" | "InvoiceLineItem") || undefined,
    sortDir: sorting[0]?.desc ? "desc" : "asc",
  });
  const { data } = oofQuery;
  const flatData = useMemo(() => data?.rows.map((page) => page) ?? [], [data]);
  const count = data?.count ?? 0;

  const pageCount = Math.ceil(count / pagination.pageSize);

  const handleTableSort = (columnSort: string | null) => {
    const [id, dir] = columnSort?.split("_") || [];
    const desc = dir === "desc";

    if (id && (sorting[0]?.id !== id || sorting[0].desc !== desc)) {
      setSorting([{ id, desc }]);
    }
  };

  const columnHelper = createSellarColumnHelper<ColumnDataType>();

  const isWaived = (row: { waivedAt?: Date | null }) => !!row.waivedAt;

  const columns = [
    columnHelper.accessor("createdAt", {
      id: "createdAt",
      header: "Fee date",
      enableSorting: true,
      cell: (info) => {
        const createdAt = info.getValue();
        return (
          <span className={isWaived(info.row.original) ? "line-through" : ""}>
            {createdAt ? dayjs(createdAt).format("DD/MM/YYYY") : "NA"}
          </span>
        );
      },
    }),
    columnHelper.accessor("Order", {
      id: "orderId",
      header: "Order #",
      enableSorting: false,
      cell: (info) => {
        const orderId = info.getValue().id || "NA";
        return (
          <span className={isWaived(info.row.original) ? "line-through" : ""}>
            {orderId}
          </span>
        );
      },
    }),
    columnHelper.accessor("Order", {
      id: "customer",
      header: "Customer",
      enableSorting: false,
      cell: (info) => {
        const customer = info.getValue().Retailer.Business.name || "NA";
        return (
          <span className={isWaived(info.row.original) ? "line-through" : ""}>
            {customer}
          </span>
        );
      },
    }),
    columnHelper.accessor("fee", {
      id: "fee",
      header: "Fee amount",
      enableSorting: false,
      cell: (info) => {
        const amount = info.getValue() || 0;
        return (
          <span className={isWaived(info.row.original) ? "line-through" : ""}>
            {formatCurrency(amount)}
          </span>
        );
      },
    }),
    columnHelper.accessor("InvoiceLineItem", {
      id: "InvoiceLineItem",
      header: "Invoice date",
      enableSorting: true,
      cell: (info) => {
        const createdAt = info.getValue()?.createdAt;
        return (
          <span className={isWaived(info.row.original) ? "line-through" : ""}>
            {createdAt ? dayjs(createdAt).format("DD/MM/YYYY") : "NA"}
          </span>
        );
      },
    }),
    columnHelper.accessor("fee", {
      id: "notes",
      header: "Notes",
      enableSorting: false,
      cell: (info) => {
        const waived = isWaived(info.row.original);
        return waived ? `Waived` : "";
      },
    }),
    columnHelper.accessor((row) => row, {
      id: "action",
      header: "",
      enableSorting: false,
      cell: () => (
        <div className="flex w-full justify-end">
          <Icon icon="CHEVRON_RIGHT" size={1} />
        </div>
      ),
    }),
  ];

  if (oofQuery.isLoading && flatData.length === 0) {
    return <SpinnerLegacy />;
  }

  return (
    <TablePaginated
      columns={columns}
      data={flatData}
      pageCount={pageCount}
      pagination={pagination}
      setPagination={setPagination}
      columnSortName={
        sorting.length
          ? `${sorting[0]?.id}_${sorting[0]?.desc ? "desc" : "asc"}`
          : undefined
      }
      handleTableSort={handleTableSort}
      onRowClick={(row) => {
        setSelectedOof(row);
        setActive(true);
      }}
      noResultsText="Your search returned 0 results"
      paginationLabel="Opening order fees per page"
      gridClasses="grid-cols-[repeat(6,_1fr)_40px]"
    />
  );
};
